<template>
  <OmtStickedModal v-if="isOpen" @close="$emit('close')">
    <template #header>
      <CBox mx="auto">
        <OmtTypography as="h1-bold" lineHeight="32px">Endereço de busca</OmtTypography>
      </CBox>
    </template>

    <CBox display="flex" flex-direction="column" gridGap="16px" mb="24px">
      <CBox
        v-if="currentLocation"
        border="0.5px solid var(--colors-light-secondary-divider)"
        border-radius="8px"
        px="16px"
        py="8px"
        cursor="pointer"
        @click="selectAddress(currentLocation.Tipo, currentLocation)"
      >
        <CFlex justify-content="space-between" align-items="center">
          <CFlex display="flex" align-items="center">
            <CBox
              w="16px"
              h="16px"
              border-radius="50%"
              :bg="locationDisabled ? 'rgba(255, 116, 0, 0.1)' : 'rgba(28, 160, 255, 0.15)'"
              display="flex"
              align-items="center"
              justify-content="center"
              mr="10px"
            >
              <CBox
                :bg="locationDisabled ? '#FF7400' : 'var(--colors-light-support-news)'"
                w="8px"
                h="8px"
                border-radius="50%"
              />
            </CBox>
            <CBox>
              <OmtTypography as="h3-semi-bold" mb="4px">{{ currentLocation.Titulo }}</OmtTypography>
              <OmtTypography v-if="locationDisabled" as="p2" mt="4px" color="var(--colors-light-primary-gray1)">
                Localização desativada. Precisamos acessá-la para encontrar prestadores próximos a você.
              </OmtTypography>
              <OmtTypography v-else as="p2" v-for="part in splitDescription(currentLocation.Descricao)" :key="part">
                {{ part }}
              </OmtTypography>
            </CBox>
          </CFlex>
          <CFlex align-items="center">
            <OmtRadio name="location" :is-checked="isSelected(currentLocation)" :navyBlue="true" />
          </CFlex>
        </CFlex>
      </CBox>

      <CBox
        v-for="addr in savedAddresses"
        :key="addr.Codigo || addr.Tipo"
        border="0.5px solid var(--colors-light-secondary-divider)"
        border-radius="8px"
        px="16px"
        py="8px"
        cursor="pointer"
        @click="selectAddress(addr.Tipo, addr)"
      >
        <CFlex justify-content="space-between" align-items="center">
          <CFlex display="flex" align-items="center">
            <OmtIcons :name="getAddressIcon(addr)" size="1x" mr="10px" />
            <CBox>
              <OmtTypography as="h3-semi-bold" mb="4px">{{ addr.Titulo }}</OmtTypography>
              <OmtTypography v-if="addr.Tipo === 'outra-cidade'" as="p2"> Escolha o estado e cidade </OmtTypography>
              <OmtTypography v-else as="p2" v-for="part in splitDescription(getAddress(addr))" :key="part">
                {{ part }}
              </OmtTypography>
            </CBox>
          </CFlex>
          <CFlex align-items="center">
            <OmtRadio name="location" :is-checked="isSelected(addr)" :navyBlue="true" />
          </CFlex>
        </CFlex>
      </CBox>
    </CBox>
  </OmtStickedModal>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ModalSearchAddress",
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState("affiliate", ["addresses", "locationDisabled", "enderecoSelecionado"]),
    currentLocation() {
      return this.addresses.find(endereco => endereco.Tipo === "atual") || null;
    },
    savedAddresses() {
      return this.addresses.filter(endereco => endereco.Tipo !== "atual");
    },
    initialSelectedType() {
      if (this.enderecoSelecionado && this.enderecoSelecionado.Tipo) {
        if (this.enderecoSelecionado.Tipo === "outra-cidade-selecionada") {
          return "outra-cidade";
        }

        // Verifica se está desabilitado e se tem uma opção salva válida
        if (
          (this.enderecoSelecionado.Tipo === "atual" || this.enderecoSelecionado.Tipo === "Atual") &&
          this.locationDisabled
        ) {
          const firstSavedAddress =
            this.savedAddresses.find(addr => addr.Tipo !== "outra-cidade") || this.savedAddresses[0];
          return firstSavedAddress?.Tipo || "";
        }

        return this.enderecoSelecionado.Tipo;
      }

      const selectedAddress = this.addresses.find(addr => addr.Selecionado);

      // Verifica se está desabilitado e se tem uma opção salva válida
      if ((selectedAddress?.Tipo === "atual" || selectedAddress?.Tipo === "Atual") && this.locationDisabled) {
        const firstSavedAddress =
          this.savedAddresses.find(addr => addr.Tipo !== "outra-cidade") || this.savedAddresses[0];
        return firstSavedAddress?.Tipo || "";
      }

      return selectedAddress?.Tipo || "";
    }
  },
  data() {
    return {
      selectedType: ""
    };
  },
  watch: {
    isOpen: {
      immediate: true,
      handler() {
        this.selectedType = this.initialSelectedType;
      }
    },
    initialSelectedType(value) {
      this.selectedType = value;
    }
  },
  methods: {
    async selectAddress(type, data = null) {
      if ((type === "atual" || type === "Atual") && this.locationDisabled) {
        this.$root.$emit("show-loading");
        try {
          await this.$store.dispatch("affiliate/requestUserLocation");
          if (this.$store.state.affiliate.locationDisabled) {
            this.$toast({
              description: "Não foi possível obter sua localização. Por favor, permita o acesso no navegador.",
              status: "warning",
              duration: 5000,
              position: "top"
            });
            return;
          }
        } finally {
          this.$root.$emit("hide-loading");
        }
      }
      this.selectedType = type;
      this.$emit("select", { type, data });
    },
    isSelected(address) {
      return this.selectedType === address?.Tipo;
    },
    getAddressIcon(address) {
      switch (address?.Tipo) {
        case "casa":
          return "house";
        case "trabalho":
          return "briefcase";
        case "outra-cidade":
          return "circle-plus";
        default:
          return "map-pin";
      }
    },
    getAddress(address) {
      return address.Codigo !== "outra-cidade" && address.Codigo !== "atual"
        ? address?.EnderecoFormatado
        : address.Descricao || "";
    },
    splitDescription(desc) {
      if (!desc) return [];
      return desc
        .split("-")
        .map(part => part.trim())
        .filter(part => part);
    }
  }
};
</script>
